import { SUPPORT_EMAIL } from './content'

export const FIELD_DOCS_CAMPAIGN = 'field-docs-v1' as const

/**
 * 가격이 바뀌면 반드시 버전을 올린다 — 안 올리면 옛 가격에 눌린 클릭과 새 가격 클릭이
 * 한 계열로 합쳐져 어느 가격이 먹혔는지 영영 못 가른다.
 * v1 = 월 19,900 / 연 129,000 (한 번도 노출된 적 없다 — 랜딩이 구현되기 전에 폐기됐다)
 * v2 = 월 49,000 / 연 490,000 (2026-08-11 사용자 결정)
 */
export const FIELD_DOCS_PRICE_VERSION = 'kr-2026-08-v2' as const

/**
 * 표시 가격. 경로에 박히는 숫자와 화면에 찍히는 숫자가 갈라지면 집계가 거짓이 되므로
 * 여기 한 곳에서만 정한다. **바꾸면 `nginx.conf` 의 allowlist 정규식도 같이 고쳐야 한다** —
 * 안 고치면 nginx 가 404 로 떨궈 클릭이 통째로 안 세어진다.
 */
export const FIELD_DOCS_PRICE = { monthly: 49_000, yearly: 490_000 } as const

export type FieldDocsSource = 'direct' | 'kakao' | 'sms' | 'email' | 'community'
export type FieldDocsTrade =
  'cleaning' | 'detailing' | 'handyman' | 'landscaping' | 'other'
export type FieldDocsRole = 'owner' | 'operator' | 'other'
export type FieldDocsWeeklyJobs = '1-5' | '6-10' | '11-30' | '31-plus'
export type FieldDocsMethod = 'paper' | 'spreadsheet' | 'messenger' | 'none'
export type FieldDocsBilling = 'monthly' | 'yearly'

export type FieldDocsEvent =
  | {
      name: 'landing_view'
      invite: string
      source: FieldDocsSource
      trade: FieldDocsTrade
      qualifiedOwner: boolean
    }
  | { name: 'sample_pdf_open'; invite: string; trade: FieldDocsTrade }
  | { name: 'pricing_view'; invite: string }
  | { name: 'plan_click'; invite: string; billing: FieldDocsBilling }
  | { name: 'beta_form_start'; invite: string; trade: FieldDocsTrade }
  | {
      name: 'beta_form_submit'
      invite: string
      trade: FieldDocsTrade
      role: FieldDocsRole
      weeklyJobs: FieldDocsWeeklyJobs
      method: FieldDocsMethod
    }
  | {
      name: 'interview_book'
      invite: string
      trade: FieldDocsTrade
      weeklyJobs: FieldDocsWeeklyJobs
    }

/**
 * 초대코드를 뺀 이벤트 — 화면 쪽은 초대코드를 매번 넘기지 않고 한 곳에서 붙인다.
 *
 * `Omit<FieldDocsEvent, 'invite'>` 를 그냥 쓰면 안 된다. 유니언에 Omit 을 씌우면 **모든 갈래의
 * 공통 키만 남아** `source`·`billing` 같은 갈래 전용 속성이 통째로 사라진다. 조건부 타입으로
 * 분배해야 갈래별 모양이 유지된다.
 */
export type FieldDocsEventInput =
  FieldDocsEvent extends infer E ? (E extends FieldDocsEvent ? Omit<E, 'invite'> : never) : never

const INVITE_PATTERN = /^[a-z0-9]{20}$/

/**
 * 48시간 가격 검증 이벤트를 first-party access log용 경로로 직렬화한다.
 *
 * 초대코드와 모든 속성은 고정 길이/enum이라 이름·전화번호·이메일·자유서술이 들어갈
 * 자리가 없다. IP와 User-Agent는 앞단 nginx 운영 로그 정책(14일)을 그대로 따른다.
 */
export function fieldDocsEventPath(event: FieldDocsEvent): string {
  if (!INVITE_PATTERN.test(event.invite)) {
    throw new Error('현장서류 초대코드는 소문자 영숫자 20자리여야 한다')
  }

  const root = `/_events/field-docs/v1/${event.name}/${event.invite}`
  switch (event.name) {
    case 'landing_view':
      return `${root}/${event.source}/${FIELD_DOCS_CAMPAIGN}/${event.trade}/${event.qualifiedOwner ? 'yes' : 'no'}`
    case 'sample_pdf_open':
      return `${root}/${event.trade}`
    case 'pricing_view':
      return `${root}/${FIELD_DOCS_PRICE_VERSION}`
    case 'plan_click':
      return `${root}/solo/${event.billing}/${FIELD_DOCS_PRICE[event.billing]}`
    case 'beta_form_start':
      return `${root}/solo/${event.trade}`
    case 'beta_form_submit':
      return `${root}/solo/${event.trade}/${event.role}/${event.weeklyJobs}/${event.method}`
    case 'interview_book':
      return `${root}/${event.trade}/${event.weeklyJobs}`
  }
}

/** 빈 body의 same-origin POST만 보낸다. 실패해도 실험 화면의 본 작업을 막지 않는다. */
export function sendFieldDocsEvent(event: FieldDocsEvent): boolean {
  return navigator.sendBeacon(fieldDocsEventPath(event))
}

/**
 * 초대코드 없이 들어온 사람이 **직접 보내는** 신청 메일. 링크만 만들고 여는 것은 본인이다.
 *
 * 왜 폼 전송이 아니라 mailto 인가 — 이 랜딩은 개인정보를 하나도 안 받는 것이 설계다
 * (이름·상호·이메일 입력란이 없다). 그런데 아웃바운드로만 쓰던 탓에 검색으로 들어온 사람은
 * **닿을 방법이 없어 그대로 증발했다.** 메일 링크는 그 구멍만 메운다: 저장하는 것도 우리가
 * 보내는 것도 없고, 주소를 아는 쪽은 처음부터 끝까지 본인이다.
 *
 * 본문에 싣는 것은 폼에서 고른 enum 넷뿐이다 — 사람을 특정하는 값은 넣지 않는다.
 */
export function betaMailto(answers: {
  trade: FieldDocsTrade
  role: FieldDocsRole
  weeklyJobs: FieldDocsWeeklyJobs
  method: FieldDocsMethod
}): string {
  const body = [
    'beta',
    '',
    `trade: ${answers.trade}`,
    `role: ${answers.role}`,
    `weekly jobs: ${answers.weeklyJobs}`,
    `documents today: ${answers.method}`,
  ].join('\n')
  return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
    'Field docs beta',
  )}&body=${encodeURIComponent(body)}`
}
